import { Link } from "react-router-dom";
import { CheckCircle2, CircleDashed, Clapperboard, FileText, Image as ImageIcon, Layers, Loader2, Megaphone, TriangleAlert, User } from "lucide-react";

import type { AdvisorBriefing, BriefingRunningItem } from "../api/advisorBriefingApi";
import { isSafeInternalRoute } from "../api/advisorBriefingApi";
import { fallbackGreeting } from "../model/useAdvisorBriefing";
import { usePersona } from "@/shared/brand/persona";

/**
 * ADV-UI-2 满屏页开屏三块(老板圈定):①干练伙计式真招呼 ②「店里正在跑的事」任务卡横排
 * ③建议 chips(点击=把话填进输入框走自动路由,**不直接派单**)。
 *
 * ★头号靶「开屏是白的体感不好」:loading 全程骨架屏占位(shimmer),三块的版面从第一帧就在;
 * 拉取失败/超时 → 招呼降级为纯前端时段问候,任务卡/chips 整块隐藏 —— 失败也有一张脸,绝不白屏。
 * 文案纪律:greeting/line/title 是 ai 拼好的成品人话,这里零加工原样渲染。
 */

const KIND_ICON: Record<BriefingRunningItem["kind"], typeof ImageIcon> = {
  poster: ImageIcon,
  video: Clapperboard,
  talking_head: User,
  article: FileText,
  campaign: Megaphone,
  blueprint: Layers,
};

const STATE_BADGE: Record<BriefingRunningItem["state"], { label: string; cls: string; Icon: typeof Loader2 }> = {
  running: { label: "进行中", cls: "text-primary", Icon: Loader2 },
  just_done: { label: "刚完成", cls: "text-emerald-600 dark:text-emerald-400", Icon: CheckCircle2 },
  failed_unbilled: { label: "没做成(未扣分)", cls: "text-destructive", Icon: TriangleAlert },
  partial: { label: "部分完成", cls: "text-amber-600 dark:text-amber-400", Icon: CircleDashed },
};

function Shimmer({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-md bg-muted ${className}`} aria-hidden />;
}

/** 骨架屏:三块版面同构占位,首帧即在(测试/e2e 判据钉 data-testid)。 */
export function BriefingSkeleton() {
  const persona = usePersona();
  return (
    <div className="space-y-4" data-testid="briefing-skeleton" aria-label={`${persona.title}开屏加载中`}>
      <div className="space-y-2">
        <Shimmer className="h-5 w-3/5" />
        <Shimmer className="h-5 w-2/5" />
      </div>
      <div className="flex gap-3">
        <Shimmer className="h-24 w-56 shrink-0" />
        <Shimmer className="h-24 w-56 shrink-0" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Shimmer className="h-8 w-40" />
        <Shimmer className="h-8 w-32" />
        <Shimmer className="h-8 w-36" />
      </div>
    </div>
  );
}

function RunningCard({ item }: { item: BriefingRunningItem }) {
  const Icon = KIND_ICON[item.kind] ?? Layers;
  const badge = STATE_BADGE[item.state] ?? STATE_BADGE.running;
  const body = (
    <>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate text-sm font-semibold">{item.title}</span>
      </div>
      <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.line}</div>
      <div className={`mt-2 flex items-center gap-1 text-2xs font-medium ${badge.cls}`}>
        <badge.Icon
          className={`h-3 w-3 ${item.state === "running" ? "animate-spin" : ""}`}
          aria-hidden
        />
        {badge.label}
      </div>
    </>
  );
  const cls =
    "w-56 shrink-0 rounded-xl border bg-card p-3 text-left transition-colors hover:border-primary/50";
  // route 站内白名单(契约回执①):脏值/外链 → 卡片降级为不可点静态块,绝不跳出站。
  return isSafeInternalRoute(item.route) ? (
    <Link to={item.route} className={cls} data-testid={`briefing-card-${item.id}`}>
      {body}
    </Link>
  ) : (
    <div className={cls} data-testid={`briefing-card-${item.id}`}>
      {body}
    </div>
  );
}

export function AdvisorBriefing({
  briefing,
  loading,
  failed,
  onChipPick,
}: {
  briefing: AdvisorBriefing | null;
  loading: boolean;
  failed: boolean;
  /** chips 点击 = 把话**填进输入框**(聚焦不发送),用户回车才走自动路由 —— 老板机制红线。 */
  onChipPick: (text: string) => void;
}) {
  if (loading) return <BriefingSkeleton />;

  // 降级:拉取失败/超时 → 纯前端时段问候(不带读数不冒充),任务卡/chips 整块隐藏。
  if (failed || !briefing) {
    return (
      <div className="space-y-1" data-testid="briefing-fallback">
        <p className="text-base font-medium leading-relaxed">{fallbackGreeting()}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="briefing">
      <p className="whitespace-pre-wrap text-base font-medium leading-relaxed" data-testid="briefing-greeting">
        {briefing.greeting.text}
      </p>

      {briefing.runningItems.length > 0 && (
        <div>
          <div className="mb-1.5 text-xs font-semibold text-muted-foreground">店里正在跑的事</div>
          <div className="flex gap-3 overflow-x-auto overscroll-x-contain pb-1">
            {briefing.runningItems.slice(0, 3).map((it) => (
              <RunningCard key={it.id} item={it} />
            ))}
          </div>
        </div>
      )}

      {briefing.chips.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="briefing-chips">
          {briefing.chips.slice(0, 4).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onChipPick(c.text)}
              className="rounded-full border bg-card px-3 py-1.5 text-xs text-foreground/80 transition-colors hover:border-primary/60 hover:text-primary"
            >
              {c.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
